import { Search, Star } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { daysUntil, initials } from "@/lib/trip";

export const demoToast = (feature) => toast(`${feature} is a demo`, { description: "This action isn't active in the prototype." });

// Keep phone numbers as plain digits (strip auto-inserted dashes/spaces/parens from browser autofill); allow a single leading +.
export const normalizePhone = (v) => {
  const s = (v || "").trimStart();
  return (s.startsWith("+") ? "+" : "") + s.replace(/\D/g, "");
};
export const isPhoneKey = (k) => /phone/i.test(k || "");
// onBlur safety net: after the browser's own blur-time reformat, force the field back to plain digits.
// Runs on next tick so it reads the value AFTER any native autofill formatting, and rewrites the DOM
// directly (covers the React controlled-input bailout where normalized === current state).
export const normalizePhoneOnBlur = (apply) => (e) => {
  const el = e.target;
  setTimeout(() => {
    const n = normalizePhone(el.value);
    if (el.value !== n) el.value = n;
    apply(n);
  }, 0);
};

const LOGOS = {
  mytrip: { light: "/images/mytrip-logo-white.png", dark: "/images/mytrip-logo-navy.png", alt: "MyTrip", size: "h-10" },
  compass: { light: "/images/mycompass-logo-light.png", dark: "/images/mycompass-logo.png", alt: "MyCompass", size: "h-6" },
};

export function Logo({ light = false, sub, brand = "compass", size, center = false }) {
  const l = LOGOS[brand];
  return (
    <div className={cn("leading-none", center && "text-center")} data-testid={`${brand}-logo`}>
      <img src={light ? l.light : l.dark} alt={l.alt} className={cn("w-auto max-w-full select-none", center && "mx-auto", size || l.size)} draggable={false} />
      {sub && <span className={cn("mt-1.5 block text-[11px] font-medium", light ? "text-white/90 [text-shadow:0_1px_3px_rgba(0,0,0,0.6)]" : "text-slate-400")}>{sub}</span>}
    </div>
  );
}

const STATUS = {
  QUOTE: "bg-amber-50 text-amber-700 ring-amber-200/70",
  BOOKED: "bg-emerald-50 text-emerald-700 ring-emerald-200/70",
  COMPLETED: "bg-slate-100 text-slate-600 ring-slate-200",
};

export function StatusBadge({ status, className }) {
  return (
    <span data-testid={`status-badge-${status.toLowerCase()}`} className={cn("inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wider ring-1 ring-inset", STATUS[status], className)}>
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
      {status}
    </span>
  );
}

const TIER = { VIP: "bg-navy-900 text-white", Preferred: "bg-sky-50 text-sky-700", Standard: "bg-slate-100 text-slate-600" };
export const TierBadge = ({ tier }) => <span className={cn("rounded-md px-2 py-0.5 text-[11px] font-semibold", TIER[tier])}>{tier}</span>;

export const avatarUrl = (id, v) => (id && v ? `${process.env.REACT_APP_BACKEND_URL}/api/agents/${id}/avatar?v=${encodeURIComponent(v)}` : null);

export function Avatar({ name, src, size = "md", className, ...rest }) {
  const s = { sm: "h-8 w-8 text-[11px]", md: "h-10 w-10 text-xs", lg: "h-14 w-14 text-base", xl: "h-20 w-20 text-xl" }[size];
  const base = cn("grid shrink-0 place-items-center overflow-hidden rounded-full bg-navy-50 font-semibold text-navy-800 ring-1 ring-navy-100", s, className);
  if (src) return <span className={base} data-testid="avatar-image" {...rest}><img src={src} alt={name} className="h-full w-full object-cover" draggable={false} /></span>;
  return <span className={base} {...rest}>{initials(name)}</span>;
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="mb-6 flex flex-col gap-4 sm:mb-8 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        <h1 className="font-display text-2xl font-bold tracking-tight text-navy-900 sm:text-3xl" data-testid="page-title">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function Card({ className, children, ...p }) {
  return <div className={cn("rounded-2xl border border-slate-200/80 bg-white shadow-[0_1px_2px_rgba(15,30,54,0.04)]", className)} {...p}>{children}</div>;
}

export function CardTitle({ children, action }) {
  return (
    <div className="flex items-center justify-between gap-3 px-5 pt-5 sm:px-6">
      <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">{children}</h2>
      {action}
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder, testId, className }) {
  return (
    <label className={cn("relative block", className)}>
      <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-11 w-full rounded-xl border border-slate-200 bg-white pl-10 pr-4 text-base text-navy-900 outline-none transition-colors placeholder:text-slate-400 focus:border-navy-500 focus:ring-2 focus:ring-navy-100 sm:text-sm"
      />
    </label>
  );
}

export function Countdown({ date, className }) {
  const d = daysUntil(date);
  const label = d > 0 ? `${d}` : d === 0 ? "Today" : null;
  if (label === null) return null;
  return (
    <span className={cn("inline-flex flex-col items-center rounded-xl bg-white/95 px-2.5 py-1 text-navy-900 shadow-sm backdrop-blur", className)} data-testid="countdown-badge">
      <span className="font-display text-base font-bold leading-none">{label}</span>
      {d > 0 && <span className="mt-0.5 text-[9px] font-semibold uppercase tracking-widest text-slate-500">{d === 1 ? "day" : "days"}</span>}
    </span>
  );
}

export function Segmented({ options, value, onChange, testPrefix }) {
  return (
    <div className="inline-flex max-w-full gap-1 overflow-x-auto rounded-xl bg-slate-100 p-1 no-scrollbar">
      {options.map((o) => (
        <button
          key={o.value}
          data-testid={`${testPrefix}-${o.value.toLowerCase()}`}
          onClick={() => onChange(o.value)}
          className={cn("whitespace-nowrap rounded-lg px-3.5 py-1.5 text-sm font-medium transition-colors", value === o.value ? "bg-white text-navy-900 shadow-sm" : "text-slate-500 hover:text-navy-900")}
        >
          {o.label}
          {o.count !== undefined && <span className="ml-1.5 text-xs text-slate-400">{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function Stars({ value = 0, className }) {
  return (
    <span className={cn("inline-flex gap-0.5", className)} data-testid="stars-display">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={cn("h-4 w-4", value >= n ? "fill-amber-400 text-amber-400" : "text-slate-200")} />
      ))}
    </span>
  );
}

export function Empty({ icon: Icon, title, text }) {  return (
    <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
      {Icon && <Icon className="mb-3 h-6 w-6 text-slate-300" />}
      <p className="text-sm font-medium text-navy-900">{title}</p>
      {text && <p className="mt-1 text-sm text-slate-500">{text}</p>}
    </div>
  );
}

export const btn = {
  primary: "inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-navy-900 px-4 text-sm font-semibold text-white transition-colors hover:bg-navy-800 disabled:opacity-50",
  secondary: "inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-navy-900 transition-colors hover:bg-slate-50",
  ghost: "inline-flex h-9 items-center justify-center gap-1.5 rounded-lg px-3 text-sm font-medium text-navy-700 transition-colors hover:bg-slate-100",
};
